import { useState } from 'react'

import Image from 'next/image'

import { cx } from '@linaria/core'

import { ChatSpaceUser } from '~/api-definition/definition/chatSpaceUser'
import { DinnerUser } from '~/api-definition/definition/dinnerUser'
import { User } from '~/api-definition/definition/user'
import { config } from '~/config'
import { MinimumDinnerUser } from '~/libs/dinner'

export const AppImage: React.FC<{
  variant:
    | { type: 'cover'; coverPosition: 'center' }
    | { type: 'contain' }
    | {
        type: 'profile'
        user: User | ChatSpaceUser | DinnerUser | MinimumDinnerUser | undefined
      }
  alt: string
  src: string | { try?: string; fallback: string }
  rounded: 'none' | 'md' | 'full'
  width: number
  height: number
  className?: string
  imageClassName?: string
}> = (props) => {
  const [isError, setIsError] = useState<boolean>(false)

  const src = typeof props.src === 'string' ? props.src : props.src.try
  const fallbackSrc =
    typeof props.src === 'string'
      ? config.noImageUrl.restaurantCoverImage
      : props.src.fallback

  const getRankFrame = (rank: User['rank']) => {
    switch (rank) {
      case 'gold':
        return '/images/user_ranks/gold.png'
      case 'vip':
        return '/images/user_ranks/vip.png'
      case 'vvip':
        return '/images/user_ranks/vvip.png'
      default:
        return null
    }
  }

  switch (props.variant.type) {
    case 'contain':
      return (
        <div
          className={cx(
            `relative flex justify-center items-center`,
            props.className
          )}
        >
          <Image
            unoptimized
            className={cx(
              'object-contain',
              props.rounded === 'md' && 'rounded-md',
              props.rounded === 'full' && 'rounded-full',
              props.imageClassName
            )}
            alt={props.alt}
            src={isError ? fallbackSrc : src ?? fallbackSrc}
            width={props.width}
            height={props.height}
            onError={() => setIsError(true)}
          />
        </div>
      )
    case 'cover':
      return (
        <div
          className={cx('relative', props.className)}
          style={{ width: props.width, height: props.height }}
        >
          <Image
            unoptimized
            fill
            className={cx(
              'object-cover',
              props.variant.coverPosition === 'center' && 'object-center',
              props.rounded === 'md' && 'rounded-md',
              props.rounded === 'full' && 'rounded-full'
            )}
            alt={props.alt}
            src={isError ? fallbackSrc : src ?? fallbackSrc}
            onError={() => setIsError(true)}
          />
        </div>
      )
    case 'profile': {
      // Check if user exists and showRankFrame is true
      const rankFrame =
        props.variant.user && props.variant.user.showRankFrame
          ? getRankFrame(props.variant.user.rank)
          : null

      return (
        <div
          className={cx('relative', props.className)}
          style={{ width: props.width, height: props.height }}
        >
          {/* Profile Image */}
          <Image
            unoptimized
            fill
            className={cx('object-cover', 'object-center', 'rounded-full')}
            alt={props.alt}
            src={isError ? fallbackSrc : src ?? fallbackSrc}
            onError={() => setIsError(true)}
          />

          {/* Rank Frame Image */}
          {rankFrame && props.variant.user && (
            <Image
              src={rankFrame}
              alt={`${props.variant.user.rank} frame`}
              width={props.width} // Set explicit width
              height={props.height} // Set explicit height
              className="absolute inset-0"
              style={{ objectFit: 'contain' }} // Ensures the frame fits properly
            />
          )}
        </div>
      )
    }
  }
}
